import { useCallback, useEffect, useState } from 'react';
import { api, type HubSource, type SetDestinationsInput, type SourcesOverview } from './api';

/** The Sources overview, and saving one source's destinations against it. */
export function useSources() {
  const [overview, setOverview] = useState<SourcesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = useCallback(() => {
    api.sources
      .overview()
      .then((next) => {
        setOverview(next);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const run = async (key: string, action: () => Promise<SourcesOverview | unknown>) => {
    setBusyKey(key);
    setError(null);
    try {
      const result = await action();
      if (result && typeof result === 'object' && 'sources' in result) setOverview(result as SourcesOverview);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      load();
    } finally {
      setBusyKey(null);
    }
  };

  const save = (source: HubSource, input: Omit<SetDestinationsInput, 'provider' | 'externalId'>) =>
    run(`${source.provider}:${source.externalId}`, () =>
      api.sources.setDestinations({ provider: source.provider, externalId: source.externalId, ...input }),
    );

  return {
    overview,
    error,
    busyKey,
    save,
    sync: () => run('sync', () => api.sources.sync()),
    pull: () => run('pull', () => api.sources.pull()),
  };
}
